import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/vansha/PublicNav";
import { motion } from "framer-motion";
import { OrbitSeed, GrowthRings, CurveWeave } from "@/components/vansha/Illustrations";

const STEPS = [
  { n: "01", title: "Create your space", body: "Sign up in seconds. Tell Vansha where you are — planning, pregnant, or just welcomed a baby. The whole app reshapes around you." },
  { n: "02", title: "Invite your circle", body: "One family, many hearts. Bring in the partner, grandparents, siblings, doctors. Everyone with the right permission — everyone in the loop." },
  { n: "03", title: "Track what matters", body: "Symptoms, medications, appointments, milestones, nutrition. Beautifully simple daily logging that builds into powerful insight over time." },
  { n: "04", title: "Preserve every moment", body: "Photos, letters, journal entries, voice notes. All captured. All searchable. All woven into a private, permanent family archive." },
  { n: "05", title: "Ask anything", body: "A calm AI companion trained on parenting wisdom — with six personas from Mother Coach to Sleep Coach. Answers that feel human." },
  { n: "06", title: "Receive your yearbook", body: "One tap generates a beautifully typeset PDF of your family's year. Members receive a printed hardcover annually — a keepsake for generations." },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#FBF4E8]" data-testid="how-page">
      <PublicNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#F4A72C] mb-6">How it works</p>
            <h1 className="font-serif font-semibold text-5xl lg:text-6xl leading-[1.05]" data-testid="how-title">Six steps.<br /><em className="text-[#E8552B]">One quiet flow.</em></h1>
            <p className="mt-6 text-lg text-[#3D2E5C] max-w-xl">From the first tap to the printed yearbook — how Vansha turns fragmented family life into a single, beautiful continuum.</p>
          </div>
          <div className="lg:col-span-5 flex justify-center"><GrowthRings size={360} /></div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="space-y-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="grid grid-cols-12 gap-6 py-10 border-t border-[#F5E7CE]" data-testid={`step-${s.n}`}>
              <div className="col-span-2 md:col-span-1"><span className="font-serif text-4xl text-[#E8552B]">{s.n}</span></div>
              <div className="col-span-10 md:col-span-11">
                <h2 className="font-serif text-3xl mb-3">{s.title}</h2>
                <p className="text-[#3D2E5C] text-lg max-w-2xl leading-relaxed">{s.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#1B1533] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 flex justify-center"><CurveWeave size={360} /></div>
          <div className="lg:col-span-7">
            <h2 className="font-serif font-semibold text-4xl lg:text-5xl leading-tight">Ready to <em className="text-[#E8552B]">begin?</em></h2>
            <p className="mt-6 text-lg opacity-80">Start free. Every feature open. No card required.</p>
            <Link to="/register" className="mt-8 inline-flex bg-[#F4A72C] text-[#1B1533] rounded-full px-8 py-4 font-medium hover:scale-105 transition-transform" data-testid="how-cta">Create your Vansha</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
